import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Card, message, Row, Col, Modal } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, PhoneOutlined, HomeOutlined, IdcardOutlined, UsergroupAddOutlined, ShopOutlined } from '@ant-design/icons';
import Ciudades from '../../ciudades/ciudades';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import "./login-top.css";

const { Option } = Select;

/**
 * Componente principal para gestionar el inicio de sesión y registro en la plataforma.
 * @component
 */
const LoginTopComponent = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [nombreSucursal, setNombreSucursal] = useState('');
  const [buttonCooldown, setButtonCooldown] = useState(false);
  const location = useLocation(); // Añadir este hook
  const [form] = Form.useForm();
  const [referidos, setReferidos] = useState([]);

  /**
   * Hook de efecto para verificar si se debe mostrar el registro.
   * Se ejecuta una vez al montar el componente.
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('showRegister') === 'true') {
      setIsLogin(false);
    }
  }, [location]);

  /**
   * Hook de efecto para obtener el nombre de la sucursal del usuario actual.
   * Se ejecuta una vez al montar el componente.
   */
  useEffect(() => {
    const fetchSucursal = async () => {
      const userId = localStorage.getItem('usuario_id');
      const token = localStorage.getItem('token'); // Asumiendo que necesitas el token para la autorización

      try {
        const response = await fetch(`https://99envios.app/api/online/sucursal/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (!response.ok) {
          throw new Error('Error al obtener la sucursal');
        }
        const data = await response.json();
        setNombreSucursal(data.nombre_sucursal);
      } catch (error) {
        console.error('Error al obtener la sucursal:', error);
      }
    };

    fetchSucursal();
  }, []);

  useEffect(() => {
    // Selector de referidos
    fetch('https://api.99envios.app/api/online/referente')
      .then(response => response.json())
      .then(data => setReferidos(data))
      .catch(error => console.error('Error cargando referidos:', error));
  }, []);

  const navigate = useNavigate();

  /**
   * Cambia entre el formulario de inicio de sesión y el de registro.
   */
  const toggleForm = () => {
    setIsLogin(!isLogin);
  };

  /**
   * Inicia el tiempo de espera para el botón.
   */
  const startButtonCooldown = () => {
    setButtonCooldown(true);
    setTimeout(() => {
      setButtonCooldown(false);
    }, 2000);
  };

  /**
   * Maneja el envío del formulario de inicio de sesión.
   * @param {Object} values - Valores del formulario.
   */
  const handleLogin = async (values) => {
    if (buttonCooldown) return;
    startButtonCooldown();

    try {
      const response = await axios.post('https://api.99envios.app/api/auth/login', {
        email: values.username,
        password: values.password,
      });

      const now = new Date();
      const expirationTime = new Date(now.getTime() + 6 * 3600 * 1000);

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('usuario_id', response.data.user.id);
      localStorage.setItem('rol_id', response.data.user.id_rol);
      localStorage.setItem('tipo_enlistment', response.data.user.tipo_enlistment);
      localStorage.setItem('email', response.data.user.email);
      localStorage.setItem('nivel_padre', response.data.user.nivel_padre);
      localStorage.setItem('porcentaje_comision', response.data.user.porcentaje_comision);

      // Solo guardar información de sucursal si el rol no es 8 y hay sucursales disponibles
      if (response.data.user.id_rol !== 8 && response.data.sucursales && response.data.sucursales.length > 0) {
        localStorage.setItem('nombre', response.data.sucursales[0].nombre);
        localStorage.setItem('codigoSucursal', response.data.sucursales[0].codigo_sucursal);
        localStorage.setItem('nivel_padre', response.data.sucursales[0].nivel_padre);
        localStorage.setItem('porcentaje_comision', response.data.sucursales[0].porcentaje_comision);
      }

      message.success('¡Inicio de sesión exitoso!');

      // Redirección basada en id_rol
      if (response.data.user.id_rol === 1) {
        navigate('/dashboardAdmin');
      } else if (response.data.user.id_rol === 3) {
        navigate('/dashboard99/');
      } else if (response.data.user.id_rol === 8) {
        navigate('/dashboardVendedores');
      } else if (response.data.user.id_rol === 9) {
        navigate('/picking/envios-entrantes');
      } else if (response.data.user.id_rol === 10 && response.data.user.tipo_enlistment === 8) {
        navigate('/picking/multi-enlistador');
      } else if (response.data.user.id_rol === 10 && response.data.user.tipo_enlistment === 6) {
        navigate('/picking/devolucion-bodega');
      } else if (response.data.user.id_rol === 10) {
        navigate('/picking/pedidos-enlistador');
      }
    } catch (error) {
      message.error('Error al iniciar sesión. Por favor, verifica tus credenciales.');
      console.error('Error al iniciar sesión:', error);
    }
  };

  /**
   * Formulario de inicio de sesión.
   */
  const LoginTopForm = () => (
    <Form name="login-top-form" onFinish={handleLogin} className="login-top-form">
      <p className='info-ingreso-datos'>Ingrese su correo y clave registrados en la plataforma online</p>
      <Form.Item name="username" rules={[{ required: true, message: 'Por favor ingrese su correo' }]}>
        <Input prefix={<UserOutlined />} placeholder="Correo" className="login-top-input" />
      </Form.Item>
      <Form.Item name="password" rules={[{ required: true, message: 'Por favor ingrese su contraseña' }]}>
        <Input.Password prefix={<LockOutlined />} placeholder="Contraseña" className="login-top-input" />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" block className="login-top-button">
          Entrar
        </Button>
      </Form.Item>
      <Form.Item>
        <a href="https://api.99envios.app/formulario-recuperar-contrasenia" className="login-top-link">¿Requiere una nueva contraseña?</a>
      </Form.Item>
    </Form>
  );

  /**
   * Maneja el envío del formulario de registro.
   * @param {Object} values - Valores del formulario.
   */
  const handleRegister = async (values) => {
    if (buttonCooldown) return;
    startButtonCooldown();
// Verifica directamente el valor de "terms" desde los valores del formulario
// Verifica directamente el valor de "terms" desde los valores del formulario
// Verifica directamente el valor de "terms" desde los valores del formulario
    if (!values.terms) {
      message.error('Debe aceptar los términos y condiciones para registrarse.');
      return;
    }

    try {
      const registerData = {
        nombre_apellidos: values.fullName,
        tipo_identificacion: values.idType,
        num_identificacion: values.idNumber,
        direccion: values.address,
        pais: values.country,
        ciudad_id: values.city,
        telefono: values.phone,
        correo: values.email,
        contrasena: values.password,
        nombre_sucursal: values.branchName,
        terminos_condiciones: values.terms ? 1 : 0
      };

      // Agregar código de referido solo si se seleccionó uno
      if (values.referralCode) {
        registerData.correo_referido = values.referralCode;
      }

      // Agregar ID de vendedor solo si se seleccionó uno
      if (values.sellerCode) {
        registerData.correo_vendedor = values.sellerCode;
      }

      const response = await axios.post('https://api.99envios.app/api/online/sucursal', registerData);

      if (response.data.success) {
        message.success('Registro exitoso. Por favor, inicia sesión.');
        setIsLogin(true);
      } else {
        message.error('Error en el registro. Por favor, intenta de nuevo.');
      }
    } catch (error) {
      message.error('Error en el registro. Por favor, verifica tus datos e intenta de nuevo.');
      console.error('Error al registrar:', error);
    }
  };

  const CustomCheckbox = ({ checked, onChange }) => (
    <div 
      className="custom-checkbox-container"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        userSelect: 'none',
        padding: '8px 16px',
        border: '2px solid #1890ff',
        borderRadius: '6px',
        backgroundColor: checked ? '#1890ff' : 'white',
        color: checked ? 'white' : '#1890ff',
        transition: 'all 0.3s',
        fontWeight: '500',
        marginBottom: '20px',
        fontSize: '12px'
      }}
    >
      <div 
        onClick={() => onChange({ target: { checked: !checked } })}
        style={{
          width: '20px',
          height: '20px',
          border: checked ? '2px solid white' : '2px solid #1890ff',
          borderRadius: '4px',
          marginRight: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: checked ? '#1890ff' : 'white',
          cursor: 'pointer'
        }}
      >
        {checked && (
          <svg 
            viewBox="0 0 24 24" 
            width="14" 
            height="14"
            style={{ fill: 'white' }}
          >
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        )}
      </div>
      <span>
        Aceptar{' '}
        <a href="/terminos-condiciones" target="_blank" style={{ textDecoration: 'underline' }}>
          términos y condiciones
        </a>
      </span>
    </div>
  );

  /**
   * Formulario de registro.
   */
  const RegisterTopForm = () => (
    <Form name="register-top-form" onFinish={handleRegister} className="register-top-form" form={form}>
      <Form.Item name="fullName" rules={[{ required: true, message: 'Por favor ingrese su nombre y apellidos' }]}>
        <Input prefix={<UserOutlined />} placeholder="Nombre y apellidos" className="login-top-input" />
      </Form.Item>
      <Row gutter={8} align="middle">
        <Col span={8}>
          <Form.Item name="idType" initialValue="CC" rules={[{ required: true, message: 'Por favor seleccione el tipo de identificación' }]}>
            <Select placeholder="Tipo ID">
              <Option value="CC">CC</Option>
              <Option value="CE">CE</Option>
              <Option value="RUT">RUT</Option>
              <Option value="NIT">NIT</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item name="idNumber" rules={[{ required: true, message: 'Por favor ingrese su número de identificación' }]}>
            <Input prefix={<IdcardOutlined />} placeholder="Número identificación" className="id-number-input" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="address" rules={[{ required: true, message: 'Por favor ingrese su dirección' }]}>
        <Input prefix={<HomeOutlined />} placeholder="Escribe tu dirección" className="login-top-input" />
      </Form.Item>
      <Form.Item name="country" initialValue="1" rules={[{ required: true, message: 'Por favor seleccione su país' }]}>
        <Select placeholder="Selecciona tu país">
          <Option value="1">Colombia</Option>
        </Select>
      </Form.Item>
      <Form.Item name="city" rules={[{ required: true, message: 'Por favor seleccione su ciudad' }]}>
        <Select showSearch
          placeholder="Selecciona una ciudad"
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }>
          {Ciudades.map((ciudad) => (
            <Option key={ciudad.value} value={ciudad.value}>
              {ciudad.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name="phone" rules={[{ required: true, message: 'Por favor ingrese su teléfono' }]}>
        <Input prefix={<PhoneOutlined />} placeholder="Teléfono" className="login-top-input" />
      </Form.Item>
      <Form.Item name="branchName" rules={[{ required: true, message: 'Por favor ingrese el nombre de la sucursal' }]}>
        <Input prefix={<HomeOutlined />} placeholder="Nombre Tienda" className="login-top-input" />
      </Form.Item>
      <Form.Item name="email" rules={[{ required: true, type: 'email', message: 'Por favor ingrese un correo válido' }]}>
        <Input prefix={<MailOutlined />} placeholder="Correo" className="login-top-input" />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          { required: true, message: 'Por favor ingrese su contraseña' },
          { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' }
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder="Contraseña"
          className="login-top-input"
          
        />
      </Form.Item>
      <Form.Item name="referralCode">
        <Input 
          prefix={<UsergroupAddOutlined />} 
          placeholder="Correo de referido (Opcional)" 
          className="login-top-input optional-input" 
          style={{fontSize: '12px'}}
        />
      </Form.Item>

      <Form.Item name="sellerCode">
        <Input 
          prefix={<ShopOutlined />} 
          placeholder="Correo de vendedor (Opcional)" 
          className="login-top-input optional-input" 
          style={{fontSize: '12px'}}
        />
      </Form.Item>
      <Form.Item
        name="terms"
        valuePropName="checked"
        rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject('Debe aceptar los términos y condiciones para registrarse') }]}
      >
        <CustomCheckbox
          checked={form.getFieldValue('terms')}
          onChange={(e) => form.setFieldsValue({ terms: e.target.checked })}
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          block
          className="login-top-button"
          disabled={buttonCooldown}
        >
          {buttonCooldown ? 'Espere 2 segundos...' : 'Registrarse'}
        </Button>
      </Form.Item>
    </Form>
  );

  return (
    <div className="login-top-wrapper">
      <div className={`login-top-container ${isLogin ? 'login-mode' : 'register-mode'}`}>
        <div className="login-top-section">
          <Card
            title={isLogin ? "Iniciar Sesión" : "Registrarse"}
            className="login-top-card"
          >
            {isLogin ? <LoginTopForm /> : <RegisterTopForm />}
          </Card>
        </div>
        <div className="register-top-section">
          <h2>{isLogin ? "¿Aún no tienes una cuenta?" : "¿Ya tienes una cuenta?"}</h2>
          <p>{isLogin ? "Regístrate para que puedas iniciar sesión" : "Inicia sesión para entrar en la página"}</p>
          <Button onClick={toggleForm} className="login-top-button">
            {isLogin ? "Registrarse" : "Iniciar Sesión"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoginTopComponent;